<template>
  <!-- Pendant la commande (adresse de pose connue), le dialogue s'efface : jamais une fenêtre sur une autre. -->
  <v-dialog :value="value && !ordering" max-width="880" persistent @input="$emit('input', $event)">
    <v-card>
      <v-card-title class="text-h6">
        {{ $t('onboarding.members.create-invitation') }}
        <v-spacer />
        <v-btn icon :aria-label="$t('close')" @click="close"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>
      <v-divider />

      <!-- Le choix : une personne, ou un fichier -->
      <v-card-text v-if="step === 'choice'" class="pt-6">
        <p class="body-2">{{ $t('onboarding.members.invite.explained') }}</p>
        <!-- La politique de recharge : ce que recevront la ou les personnes invitées, toujours dite, en clair -->
        <v-select
            :value="chosenPolicyId" :items="policies" item-text="name" item-value="id"
            :label="$t('onboarding.members.invite.policy')" :hint="$t('onboarding.members.invite.policy-hint')" persistent-hint
            :readonly="policies.length < 2" :append-icon="policies.length < 2 ? '' : '$dropdown'"
            outlined dense class="mb-2"
            @change="choosePolicy"
        >
          <template #item="{ item }">
            {{ item.name }}<v-chip v-if="item.default" x-small outlined color="primary" class="ml-2">{{ $t('onboarding.charging-policy.list.default') }}</v-chip>
          </template>
        </v-select>
        <div v-if="policySummary.length" class="invitation__policy mb-4">
          <div v-for="(line, i) in policySummary" :key="i" class="text-body-2">{{ line }}</div>
        </div>
        <v-row>
          <v-col cols="12" md="6">
            <v-card outlined hover class="pa-5 fill-height" @click="step = 'person'">
              <v-icon color="primary" large>mdi-account-outline</v-icon>
              <div class="text-subtitle-1 font-weight-medium mt-2">{{ $t('onboarding.members.invite.person') }}</div>
              <div class="text-caption text--secondary">{{ $t('onboarding.members.invite.person-hint') }}</div>
            </v-card>
          </v-col>
          <v-col cols="12" md="6">
            <v-card outlined hover class="pa-5 fill-height" @click="step = 'file'">
              <v-icon color="primary" large>mdi-file-delimited-outline</v-icon>
              <div class="text-subtitle-1 font-weight-medium mt-2">{{ $t('onboarding.members.invite.file') }}</div>
              <div class="text-caption text--secondary">{{ $t('onboarding.members.invite.file-hint') }}</div>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Une personne -->
      <v-form v-if="step === 'person'" ref="personForm" @submit.prevent="invitePerson">
        <v-card-text class="pt-6">
          <v-row dense>
            <v-col cols="12" md="6"><v-text-field v-model="person.firstname" :label="$t('firstname')" outlined dense /></v-col>
            <v-col cols="12" md="6"><v-text-field v-model="person.lastname" :label="$t('lastname')" outlined dense /></v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="person.email" :label="$t('email') + ' *'" type="email" outlined dense :rules="[v => /.+@.+\..+/.test(v || '') || $t('onboarding.members.invite.email-invalid')]" />
            </v-col>
            <v-col cols="12" md="6"><v-text-field v-model="person.phone" :label="$t('phone')" outlined dense /></v-col>
          </v-row>
          <!-- Ce qui sera commandé dès son arrivée : par défaut ce que dit la politique ; les commandes partent quand elle est membre.
               Un seul équipement compte sa recharge à domicile : un choix, pas un cumul. -->
          <div class="text-body-2 mt-1 mb-2">{{ $t('onboarding.members.invite.ordered-on-arrival') }}</div>
          <v-row dense>
            <v-col v-if="homeItems.length" cols="12" md="6">
              <v-select
                  v-model="homeMeter" :items="homeItems"
                  :label="$t('onboarding.members.invite.home-meter')"
                  :hint="$t('onboarding.members.invite.home-meter-hint')" persistent-hint
                  outlined dense
              />
            </v-col>
            <v-col v-if="otherItems.length" cols="12" :md="homeItems.length ? 6 : 12">
              <v-select
                  v-model="otherEquipment" :items="otherItems"
                  :label="$t('onboarding.members.invite.other-equipment')"
                  :hint="$t('onboarding.members.invite.equipment-hint')" persistent-hint
                  multiple small-chips outlined dense
              />
            </v-col>
          </v-row>
          <!-- Les badges que l'organisation remet elle-même : choisis dès maintenant, ils se rattachent à l'adhésion -->
          <template v-if="badgesHandedOver">
            <div class="text-body-2 mt-3 mb-1">{{ $t('onboarding.members.invite.badges-title') }}</div>
            <v-row dense>
              <v-col v-if="badgeNeeded('professional')" cols="12" md="6">
                <v-text-field v-model="person.badges.professional" :label="$t('onboarding.commissioning.badge-supply.professional')"
                              :hint="$t('onboarding.members.invite.badges-hint')" persistent-hint outlined dense />
              </v-col>
              <v-col v-if="badgeNeeded('personal')" cols="12" md="6">
                <v-text-field v-model="person.badges.personal" :label="$t('onboarding.commissioning.badge-supply.personal')"
                              :hint="$t('onboarding.members.invite.badges-hint')" persistent-hint outlined dense />
              </v-col>
            </v-row>
          </template>
          <!-- Type de membre et groupes : remplis avec ceux de la politique, à changer pour cette personne seulement -->
          <v-row dense class="mt-2">
            <v-col cols="12" md="6">
              <v-select v-model="person.memberType" :items="memberTypes" item-text="name" item-value="id" clearable
                        :label="$t('onboarding.members.invite.member-type')" :placeholder="$t('onboarding.members.invite.organization-default')"
                        :hint="hintFor('membership.member-type', person.memberType)" persistent-hint persistent-placeholder outlined dense />
            </v-col>
            <v-col cols="12" md="6">
              <v-select v-model="person.groups" :items="groups" item-text="name" item-value="id" multiple small-chips
                        :label="$t('onboarding.members.invite.groups')" :placeholder="$t('onboarding.members.invite.organization-default')"
                        :hint="hintFor('membership.groups', person.groups)" persistent-hint persistent-placeholder outlined dense />
            </v-col>
          </v-row>
          <!-- L'adresse de pose est connue : la borne (ou le compteur) se commande tout de suite, et la commande l'invite. -->
          <v-checkbox
              v-if="canOrder" v-model="withOrder" dense hide-details="auto" class="mt-2"
              :label="$t('onboarding.members.invite.with-order')" :hint="$t('onboarding.members.invite.with-order-hint')" persistent-hint
          />
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-btn small text @click="step = 'choice'">{{ $t('back') }}</v-btn>
          <v-spacer />
          <v-btn small color="primary" type="submit" :loading="working">{{ $t(withOrder ? 'onboarding.members.invite.continue-to-order' : 'onboarding.members.invite.send') }}</v-btn>
        </v-card-actions>
      </v-form>

      <!-- Un fichier : déposer, vérifier, envoyer -->
      <template v-if="step === 'file' || step === 'preview'">
        <v-card-text class="pt-4">
          <v-stepper :value="step === 'file' ? 1 : 2" alt-labels flat class="mb-2">
            <v-stepper-header>
              <v-stepper-step step="1" :complete="step === 'preview'">{{ $t('onboarding.members.import.step-file') }}</v-stepper-step>
              <v-divider />
              <v-stepper-step step="2">{{ $t('onboarding.members.import.step-check') }}</v-stepper-step>
              <v-divider />
              <v-stepper-step step="3">{{ $t('onboarding.members.import.step-send') }}</v-stepper-step>
            </v-stepper-header>
          </v-stepper>

          <template v-if="step === 'file'">
            <v-file-input v-model="file" accept=".csv,text/csv" outlined prepend-icon="mdi-file-delimited-outline" :label="$t('onboarding.members.import.choose')" show-size />
            <div class="text-caption">
              {{ $t('onboarding.members.import.columns') }}
              <a href="#" @click.prevent="downloadTemplate">{{ $t('onboarding.members.import.template') }}</a>
            </div>
          </template>

          <template v-else-if="preview">
            <div class="d-flex flex-wrap mb-3" style="gap: 8px">
              <v-chip label color="green lighten-5" text-color="green darken-4">{{ $tc('onboarding.members.import.ready', counts.valid, { count: counts.valid }) }}</v-chip>
              <v-chip label color="orange lighten-5" text-color="orange darken-4">{{ $tc('onboarding.members.import.known', counts.known, { count: counts.known }) }}</v-chip>
              <v-chip label color="red lighten-5" text-color="red darken-4">{{ $tc('onboarding.members.import.errors', counts.error, { count: counts.error }) }}</v-chip>
            </div>
            <v-data-table :headers="previewHeaders" :items="previewRows" dense :items-per-page="8" :footer-props="{ 'items-per-page-options': [8, 25, 100] }">
              <template #item.status="{ item }">
                <span :class="resultClass(item.status)">{{ resultLabel(item) }}</span>
              </template>
            </v-data-table>
            <p class="text-caption text--secondary mt-2 mb-0">{{ $t('onboarding.members.import.errors-not-sent') }}</p>
          </template>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-btn small text @click="step === 'file' ? step = 'choice' : resetFile()">
            {{ step === 'file' ? $t('back') : $t('onboarding.members.import.change-file') }}
          </v-btn>
          <v-spacer />
          <v-btn small v-if="step === 'file'" color="primary" :disabled="!file" :loading="working" @click="checkFile">
            {{ $t('onboarding.members.import.check') }}
          </v-btn>
          <v-btn small v-else color="primary" :disabled="!counts.valid" :loading="working" @click="confirmImport">
            {{ $tc('onboarding.members.import.send', counts.valid, { count: counts.valid }) }}
          </v-btn>
        </v-card-actions>
      </template>

      <!-- Envoyé -->
      <v-card-text v-if="step === 'sent'" class="text-center py-8">
        <v-icon color="success" x-large>mdi-check-circle-outline</v-icon>
        <div class="text-h6 mt-2">{{ $tc('onboarding.members.invite.sent-title', sentCount, { count: sentCount }) }}</div>
        <div class="body-2 text--secondary">{{ $t('onboarding.members.invite.sent-explained') }}</div>
        <v-btn small color="primary" class="mt-4" @click="close">{{ $t('close') }}</v-btn>
      </v-card-text>

      <!-- Adresse de pose connue : la commande, pour cette personne pas encore membre, dans cette politique ; elle l'invite -->
      <equipment-order-dialog
          v-if="canOrder"
          v-model="ordering"
          :organization-id="organizationId"
          holder="organization"
          preset-who="invitee"
          :policy-id="chosenPolicyId"
          :preset-person="ordering ? orderedPerson : null"
          :invitation="ordering ? invitationExtras() : null"
          :kinds="['charging-point', 'meter-device']"
          @created="orderCreated"
      />
    </v-card>
  </v-dialog>
</template>

<script>
import RegistrationRepository from '@repository/RegistrationRepository'
import OnboardingConfigurationRepository from '@repository/OnboardingConfigurationRepository'
import CommissioningPolicyRepository from '@repository/CommissioningPolicyRepository'
import ChargingPolicyRepository from '@repository/ChargingPolicyRepository'
import MemberTypeRepository from '@repository/MemberTypeRepository'
import MemberGroupRepository from '@repository/MemberGroupRepository'
import { orderableKinds } from '../Commissioning/policyCatalogue'
import EquipmentOrderDialog from '../Commissioning/EquipmentOrderDialog.vue'

const emptyPerson = () => ({ firstname: '', lastname: '', email: '', phone: '', memberType: null, groups: [], equipment: [], badges: { professional: '', personal: '' } })
const EQUIPMENT = 'membership.equipment'
const MEMBER_TYPE = 'membership.member-type'
const GROUPS = 'membership.groups'
const PAYMENT_METHOD = 'payment.method'
const BILLING_MODE = 'payment.billing-mode'
const HOME_KINDS = ['charging-point', 'meter-device', 'vehicle-meter']
const VEHICLE_METER = 'vehicle-meter'
const CONNECTED_VEHICLE = 'connected-vehicle'

/**
 * Créer des onboardings pour une organisation : une personne, ou un fichier. Dans les deux cas
 * on ne crée pas de membre — on crée des dossiers, chacun reçoit son e-mail et suit le parcours.
 * Le fichier est toujours vérifié ligne par ligne avant que rien ne parte.
 */
export default {
  name: 'CreateInvitationDialog',

  components: { EquipmentOrderDialog },

  props: {
    value: { type: Boolean, default: false },
    organizationId: { type: String, required: true },
    /** La politique déjà choisie (depuis sa fiche) ; absente : la politique par défaut. */
    policyId: { type: String, default: null },
  },

  data() {
    return {
      step: 'choice',
      working: false,
      person: emptyPerson(),
      memberTypes: [],
      groups: [],
      configuration: null,
      commissioning: null,
      catalogue: [],
      policies: [],
      chosenPolicyId: null,
      kinds: [],
      ordering: false,
      withOrder: false,
      file: null,
      preview: null,
      sentCount: 0,
    }
  },

  computed: {
    canOrder() {
      return (this.currentUser?.permissions || []).includes('COMMISSIONING_REQUEST')
    },

    /** La personne déjà saisie, reprise par la commande. */
    orderedPerson() {
      return { firstname: this.person.firstname, lastname: this.person.lastname, email: this.person.email.trim(), phone: this.person.phone || null }
    },

    /** La politique choisie, en clair : ses badges, sa recharge à domicile, qui paie ses recharges. */
    policySummary() {
      if (!this.configuration) return this.chosenPolicyId ? [this.$t('onboarding.members.invite.policy-unpublished')] : []
      const announced = this.configured(EQUIPMENT) || []
      const designated = this.commissioning?.settings?.['profiles.member']?.value || {}
      const kindsText = kinds => kinds.map(kind => this.$t(`onboarding.commissioning.kind.${kind}`).toLowerCase()).join(', ')
      const atArrival = HOME_KINDS.filter(kind => announced.includes(kind))
      const byManager = HOME_KINDS.filter(kind => designated[kind] && !announced.includes(kind))
      const lines = []
      lines.push(announced.includes('badge')
        ? this.$t('onboarding.charging-policy.summary.badges-announced', { badges: this.$t(`onboarding.commissioning.badge-supply.${this.commissioning?.settings?.['badges.supply']?.value || 'professional'}`) })
        : this.$t('onboarding.charging-policy.list.badges-not-announced'))
      if (atArrival.length) lines.push(this.$t('onboarding.charging-policy.summary.home', { equipment: kindsText(atArrival) }))
      if (byManager.length) lines.push(this.$t('onboarding.charging-policy.summary.home-by-manager', { equipment: kindsText(byManager) }))
      if (!atArrival.length && !byManager.length) lines.push(this.$t('onboarding.charging-policy.summary.no-home'))
      const method = this.configured(PAYMENT_METHOD)
      lines.push(method && method !== 'not-asked'
        ? this.$t('onboarding.charging-policy.list.person-pays', { billing: this.$t(`onboarding.configuration.value.${this.configured(BILLING_MODE)}`).toLowerCase() })
        : this.$t('onboarding.charging-policy.list.organization-pays'))
      return lines.map(line => line.charAt(0).toUpperCase() + line.slice(1))
    },

    /** Un badge annoncé à cette personne, que l'organisation remet elle-même (pas ZEborne par courrier). */
    badgesHandedOver() {
      return this.person.equipment.includes('badge') && this.commissioning?.settings?.['badges.dispatch']?.value === 'organization'
    },

    /** L'équipement qui compte sa recharge à domicile : un seul parmi ceux que la politique propose, ou aucun pour l'instant. */
    homeItems() {
      const kinds = this.kinds.filter(kind => HOME_KINDS.includes(kind))
      if (!kinds.length) return []
      return [
        { value: null, text: this.$t('onboarding.members.invite.home-meter-none') },
        ...kinds.map(kind => ({ value: kind, text: this.$t(`onboarding.commissioning.kind.${kind}`) })),
      ]
    },

    /** Le reste (badges, suivi du kilométrage) : à volonté ; le suivi est inclus dans un véhicule compteur. */
    otherItems() {
      return this.kinds.filter(kind => !HOME_KINDS.includes(kind)).map(kind => {
        const included = kind === CONNECTED_VEHICLE && this.homeMeter === VEHICLE_METER
        const text = this.$t(`onboarding.commissioning.kind.${kind}`)
        return { value: kind, text: included ? `${text} — ${this.$t('onboarding.members.invite.telemetry-included')}` : text, disabled: included }
      })
    },

    homeMeter: {
      get() {
        return this.person.equipment.find(kind => HOME_KINDS.includes(kind)) || null
      },
      set(kind) {
        const others = this.person.equipment.filter(k => !HOME_KINDS.includes(k) && !(kind === VEHICLE_METER && k === CONNECTED_VEHICLE))
        this.person.equipment = kind ? [kind, ...others] : others
      },
    },

    otherEquipment: {
      get() {
        return this.person.equipment.filter(kind => !HOME_KINDS.includes(kind))
      },
      set(kinds) {
        this.person.equipment = [...(this.homeMeter ? [this.homeMeter] : []), ...kinds]
      },
    },
    previewHeaders() {
      return [
        { text: this.$t('onboarding.members.import.line'), value: 'line', width: 80 },
        { text: this.$t('email'), value: 'email' },
        { text: this.$t('name'), value: 'name' },
        // La politique où entre chaque ligne : la sienne, ou celle choisie ici.
        { text: this.$t('onboarding.members.import.policy'), value: 'policy' },
        { text: this.$t('onboarding.members.import.result'), value: 'status' },
      ]
    },
    counts() {
      return this.preview?.counts || { valid: 0, known: 0, error: 0 }
    },
    // Les lignes à regarder d'abord : les erreurs, puis ce qui est déjà connu, puis le reste.
    previewRows() {
      const order = { error: 0, known: 1, valid: 2 }
      return (this.preview?.rows || [])
        .map(row => ({ ...row, name: [row.firstname, row.lastname].filter(Boolean).join(' ') }))
        .sort((a, b) => order[a.status] - order[b.status] || a.line - b.line)
    },
  },

  watch: {
    value(open) {
      if (open) {
        this.reset()
        this.loadPickers()
      }
    },
  },

  methods: {
    reset() {
      this.person = emptyPerson()
      this.withOrder = false
      this.ordering = false
      this.file = null
      this.preview = null
      this.step = 'choice'
    },

    resetFile() {
      this.file = null
      this.preview = null
      this.step = 'file'
    },

    close() {
      this.$emit('input', false)
      if (this.step === 'sent') {
        this.$emit('sent')
      }
    },

    async loadPickers() {
      const search = { organizationId: this.organizationId, offset: 0, limit: 200 }
      const [types, groups, catalogue, policies] = await Promise.all([
        MemberTypeRepository.search(search).catch(() => ({ items: [] })),
        MemberGroupRepository.search(search).catch(() => ({ items: [] })),
        OnboardingConfigurationRepository.catalogue().catch(() => []),
        ChargingPolicyRepository.list(this.organizationId).catch(() => []),
      ])
      this.memberTypes = types.items || []
      this.groups = groups.items || []
      this.catalogue = catalogue
      this.policies = policies
      // Celle qu'on a choisie avant d'ouvrir (sa fiche), sinon celle par défaut — en tête de liste.
      const chosen = policies.find(p => p.id === this.policyId) || policies.find(p => p.default) || policies[0]
      await this.choosePolicy(chosen?.id || null)
    },

    /** La commande a invité la personne : comme une invitation envoyée. */
    orderCreated() {
      this.sentCount = 1
      this.step = 'sent'
    },

    /** Les réglages de la politique choisie : ce qui est annoncé, et ce qui peut l'être. */
    async choosePolicy(policyId) {
      this.chosenPolicyId = policyId
      const [configuration, kinds, commissioning] = await Promise.all([
        OnboardingConfigurationRepository.get(this.organizationId, null, policyId).catch(() => null),
        orderableKinds(this.organizationId, policyId).catch(() => []),
        CommissioningPolicyRepository.get(this.organizationId, null, policyId).catch(() => null),
      ])
      this.configuration = configuration
      this.commissioning = commissioning
      this.kinds = kinds
      this.person.equipment = [...(this.configured(EQUIPMENT) || [])]
      // Ce que dit la politique, affiché tel quel : on le change en connaissant ce qu'il y avait.
      this.person.memberType = this.configured(MEMBER_TYPE) || null
      this.person.groups = [...(this.configured(GROUPS) || [])]
    },

    /** « Celui de la politique », ou « différent de la politique » quand on l'a changé pour cette personne. */
    hintFor(key, value) {
      const configured = this.configured(key)
      const same = Array.isArray(value) ? this.sameKinds(value, configured || []) : (value || null) === (configured || null)
      return this.$t(same ? 'onboarding.members.invite.from-policy' : 'onboarding.members.invite.differs-from-policy')
    },

    async invitePerson() {
      if (!this.$refs.personForm.validate()) {
        return
      }
      // Adresse de pose connue : la commande prend le relais, et c'est elle qui envoie l'invitation.
      if (this.withOrder) {
        this.ordering = true
        return
      }
      this.working = true
      try {
        await RegistrationRepository.create({
          source: 'form',
          organizationId: this.organizationId,
          policyId: this.chosenPolicyId,
          email: this.person.email.trim(),
          applicant: { firstname: this.person.firstname || null, lastname: this.person.lastname || null, phone: this.person.phone || null },
          ...this.invitationExtras(),
        })
        this.sentCount = 1
        this.step = 'sent'
      } catch (e) {
        this.notifyError(e)
      } finally {
        this.working = false
      }
    },

    /** Ce que l'invitation dit en plus de la personne : ce qui diffère de la politique, et les badges remis par l'organisation. */
    invitationExtras() {
      const overrides = {}
      // Seul ce qui diffère de la politique est posé pour la personne.
      if ((this.person.memberType || null) !== (this.configured(MEMBER_TYPE) || null)) overrides[MEMBER_TYPE] = this.person.memberType
      if (!this.sameKinds(this.person.groups, this.configured(GROUPS) || [])) overrides[GROUPS] = this.person.groups
      if (!this.sameKinds(this.person.equipment, this.configured(EQUIPMENT) || [])) overrides[EQUIPMENT] = this.person.equipment
      return {
        overrides,
        // Les badges que l'organisation remettra : rattachés dès que la personne est membre.
        badges: this.badgesHandedOver ? {
          professional: this.badgeNeeded('professional') ? this.person.badges.professional.trim() || null : null,
          personal: this.badgeNeeded('personal') ? this.person.badges.personal.trim() || null : null,
        } : {},
      }
    },

    /** Pro, perso ou les deux : ce que la politique fournit. */
    badgeNeeded(badge) {
      const supply = this.commissioning?.settings?.['badges.supply']?.value || 'professional'
      return supply === 'both' || supply === badge
    },

    /** La valeur de la configuration en vigueur (ou le défaut du catalogue). */
    configured(key) {
      return this.configuration?.settings?.[key]?.value ?? this.catalogue.find(d => d.key === key)?.default ?? null
    },

    sameKinds(a, b) {
      return a.length === b.length && a.every(kind => b.includes(kind))
    },

    async checkFile() {
      this.working = true
      try {
        this.preview = await RegistrationRepository.previewImport(this.organizationId, this.file, this.chosenPolicyId)
        this.step = 'preview'
      } catch (e) {
        this.notifyError(e)
      } finally {
        this.working = false
      }
    },

    async confirmImport() {
      this.working = true
      try {
        // Le serveur relit le même fichier : ce qui a changé entre-temps (un dossier ouvert ailleurs) est revérifié.
        const result = await RegistrationRepository.importFile(this.organizationId, this.file, this.chosenPolicyId)
        this.sentCount = result.created
        this.step = 'sent'
      } catch (e) {
        this.notifyError(e)
      } finally {
        this.working = false
      }
    },

    downloadTemplate() {
      RegistrationRepository.importTemplate().catch(e => this.notifyError(e))
    },

    resultClass(status) {
      return { error: 'red--text text--darken-3', known: 'orange--text text--darken-4', valid: 'green--text text--darken-4' }[status]
    },

    resultLabel(row) {
      if (row.status === 'error') {
        return (row.errors || []).map(key => (this.$te(key) ? this.$t(key, row.errorParameters?.[key]) : key)).join(' · ')
      }
      if (row.status === 'known') {
        // Pourquoi elle n'est pas envoyée : un dossier déjà ouvert, ou déjà membre.
        return row.reason && this.$te(row.reason) ? this.$t(row.reason) : this.$t('onboarding.members.import.result-known')
      }
      return this.$t('onboarding.members.import.result-ready')
    },
  },
}
</script>

<style scoped>
.invitation__policy {
  background: #f6f5f3;
  border-radius: 8px;
  padding: 10px 12px;
}
</style>
